/* The dependency graph.

   A force layout rather than a hierarchy, because the portfolio is not a tree:
   several services rest on the same methodology, and a layered drawing of that
   spends most of its space on edge crossings.

   Node colour is maturity on the same ramp as the heatmap; node size is the
   count of things depending on it. The second is the point of drawing this at
   all -- the asset half the portfolio quietly rests on is the one that becomes
   large, and it is usually not the one anybody expected. */
import { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";

import { api, type GraphData } from "../api";
import { go } from "../route";

export default function Graph() {
  const box = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<GraphData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.graph().then(setData, (e) => setError(String(e)));
  }, []);

  useEffect(() => {
    if (!box.current || !data) return;
    const chart = echarts.init(box.current);

    const key = (n: { kind: string; id: number }) => `${n.kind}:${n.id}`;
    const inbound = new Map<string, number>();
    for (const edge of data.edges) inbound.set(key(edge.to), (inbound.get(key(edge.to)) ?? 0) + 1);

    const colour = (m: number | null) => {
      if (m === null) return "rgba(120,140,160,0.35)";
      const t = Math.max(0, Math.min(100, m)) / 100;
      const r = Math.round(91 + (34 - 91) * t);
      const g = Math.round(125 + (140 - 125) * t);
      const b = Math.round(158 + (85 - 158) * t);
      return `rgba(${r},${g},${b},${(0.45 + 0.5 * t).toFixed(2)})`;
    };

    chart.setOption({
      tooltip: {
        formatter: (p: unknown) => {
          const node = (p as { data: { name: string; meta: string } }).data;
          return `<strong>${node.name}</strong><br/>${node.meta}`;
        },
      },
      series: [
        {
          type: "graph",
          layout: "force",
          roam: true,
          draggable: true,
          force: { repulsion: 320, edgeLength: 130, gravity: 0.08 },
          label: { show: true, position: "right", fontSize: 11, color: "#7e94aa" },
          edgeSymbol: ["none", "arrow"],
          edgeSymbolSize: 7,
          lineStyle: { color: "#8fa9c0", opacity: 0.55, width: 1.2, curveness: 0.08 },
          emphasis: { focus: "adjacency", lineStyle: { width: 2.4, opacity: 0.9 } },
          data: data.nodes.map((n) => ({
            id: key(n),
            name: n.name,
            symbol: n.kind === "service" ? "circle" : "diamond",
            symbolSize: 18 + 7 * (inbound.get(key(n)) ?? 0),
            itemStyle: { color: colour(n.maturity) },
            meta: `${n.class} · ${n.lifecycle_stage} · ${n.priority} priority · maturity ${Math.round(
              n.maturity ?? 0,
            )}`,
            kind: n.kind,
            oid: n.id,
          })),
          links: data.edges.map((e) => ({ source: key(e.from), target: key(e.to) })),
        },
      ],
    });

    /* ECharts types the click payload as OptionDataItem, which is deliberately
       wide; the node objects above are what actually arrive, so this narrows
       rather than casts blindly. */
    chart.on("click", (params) => {
      const node = params.data as { kind?: string; oid?: number } | null;
      if (node?.kind && node.oid) go(`/${node.kind}/${node.oid}`);
    });

    const resize = () => chart.resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      chart.dispose();
    };
  }, [data]);

  if (error) return <div className="err">{error}</div>;

  const empty = data && data.edges.length === 0;

  return (
    <section className="panel">
      <div className="panel-hd">
        <h2>Dependency graph</h2>
        <span className="muted">
          circles are services, diamonds are assets · colour is maturity · size is how many
          things depend on it
        </span>
      </div>
      {empty && (
        <p className="note">
          No dependencies have been recorded yet. The source spreadsheet carried none, so this
          graph stays empty until edges are added on the object pages.
        </p>
      )}
      <div className="graph" ref={box} />
    </section>
  );
}
